import { Decimal } from 'decimal.js'

/** An amount in integer minor units (øre, cents, satoshi-free). Never a float. */
export type Minor = number

/** A decimal string. Quantities are never JS numbers — 0.1 + 0.2 must not exist. */
export type Quantity = string

export const BASIS_POINTS_FULL = 10_000

// Ties round away from zero so a rounded amount never drifts systematically
// towards the taxman or away from him; 40 digits covers crypto quantities
// multiplied by a price in minor units without losing the tail.
const D = Decimal.clone({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -40,
  toExpPos: 40,
})

const QUANTITY_PATTERN = /^[+-]?(\d+(\.\d+)?|\.\d+)$/

export class MoneyError extends Error {}

export function isQuantity(value: string): boolean {
  return QUANTITY_PATTERN.test(value.trim())
}

function toDecimal(quantity: Quantity): Decimal {
  const trimmed = quantity.trim()
  if (!isQuantity(trimmed)) throw new MoneyError(`not a decimal string: ${JSON.stringify(quantity)}`)
  return new D(trimmed)
}

function fromDecimal(value: Decimal): Quantity {
  return value.toFixed()
}

export function assertMinor(value: number): Minor {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new MoneyError(`minor units must be an integer, got ${value}`)
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`minor units outside safe integer range: ${value}`)
  }
  return value
}

function roundToMinor(value: Decimal): Minor {
  return assertMinor(value.toDecimalPlaces(0).toNumber())
}

export function addMinor(a: Minor, b: Minor): Minor {
  return assertMinor(assertMinor(a) + assertMinor(b))
}

export function subMinor(a: Minor, b: Minor): Minor {
  return assertMinor(assertMinor(a) - assertMinor(b))
}

export function negateMinor(a: Minor): Minor {
  return assertMinor(-assertMinor(a))
}

export function sumMinor(values: Iterable<Minor>): Minor {
  let total = 0
  for (const value of values) total += assertMinor(value)
  return assertMinor(total)
}

/**
 * What a total over amounts that may be absent comes to, and how much of the
 * set it speaks for.
 */
export interface Subtotal {
  /** Null where nothing in the set carried an amount. */
  valueMinor: Minor | null
  counted: number
  absent: number
}

/**
 * A total over amounts that may be absent, which counts the absences instead of
 * adding them in as zero.
 *
 * An absent amount means nobody knows what the thing is worth, and a zero is
 * read as somebody having measured it — so the sum stops short of a figure
 * altogether where nothing in the set has one, rather than reporting the empty
 * sum. The count travels with the figure because a subtotal whose remainder is
 * counted somewhere else can disagree with it.
 */
export function subtotalMinor(values: Iterable<Minor | null>): Subtotal {
  let total = 0
  let counted = 0
  let absent = 0

  for (const value of values) {
    if (value === null) {
      absent += 1
      continue
    }
    total += assertMinor(value)
    counted += 1
  }

  return { valueMinor: counted === 0 ? null : assertMinor(total), counted, absent }
}

export function minMinor(a: Minor, b: Minor): Minor {
  return assertMinor(a) <= assertMinor(b) ? a : b
}

export function maxMinor(a: Minor, b: Minor): Minor {
  return assertMinor(a) >= assertMinor(b) ? a : b
}

/**
 * Quantity × price × optional FX, rounded exactly once at the end. Rounding any
 * intermediate product would let the error compound across a portfolio.
 */
export function valueOf(
  quantity: Quantity,
  priceMinor: Minor,
  fxRate: Quantity = '1',
): Minor {
  const product = toDecimal(quantity).times(assertMinor(priceMinor)).times(toDecimal(fxRate))
  return roundToMinor(product)
}

export function multiplyMinor(amountMinor: Minor, factor: Quantity): Minor {
  return roundToMinor(new D(assertMinor(amountMinor)).times(toDecimal(factor)))
}

export function divideMinor(amountMinor: Minor, divisor: Quantity): Minor {
  const d = toDecimal(divisor)
  if (d.isZero()) throw new MoneyError('division by zero')
  return roundToMinor(new D(assertMinor(amountMinor)).dividedBy(d))
}

export function convertMinor(amountMinor: Minor, rate: Quantity): Minor {
  return multiplyMinor(amountMinor, rate)
}

export function applyBasisPoints(amountMinor: Minor, bp: number): Minor {
  if (!Number.isInteger(bp)) throw new MoneyError(`basis points must be an integer, got ${bp}`)
  return roundToMinor(new D(assertMinor(amountMinor)).times(bp).dividedBy(BASIS_POINTS_FULL))
}

export function removeBasisPoints(amountMinor: Minor, bp: number): Minor {
  return subMinor(amountMinor, applyBasisPoints(amountMinor, bp))
}

/**
 * A quantity with a discount in basis points taken out of it.
 *
 * A discount scales the same product that price and FX do, so it belongs in the
 * quantity: the value then rounds exactly once at the end. Discounting the
 * price or the value instead rounds a second time, and a row rounded twice
 * disagrees with the total that rounded once.
 */
export function withoutBasisPoints(quantity: Quantity, bp: number): Quantity {
  if (!Number.isInteger(bp) || bp < 0 || bp > BASIS_POINTS_FULL) {
    throw new MoneyError(`discount out of range: ${bp}`)
  }
  if (bp === 0) return quantity
  return fromDecimal(
    toDecimal(quantity).times(BASIS_POINTS_FULL - bp).dividedBy(BASIS_POINTS_FULL),
  )
}

export function ratioBasisPoints(partMinor: Minor, wholeMinor: Minor): number {
  if (assertMinor(wholeMinor) === 0) return 0
  return new D(assertMinor(partMinor))
    .times(BASIS_POINTS_FULL)
    .dividedBy(wholeMinor)
    .toDecimalPlaces(0)
    .toNumber()
}

export function normalizeQuantity(quantity: Quantity): Quantity {
  return fromDecimal(toDecimal(quantity))
}

export function addQuantity(a: Quantity, b: Quantity): Quantity {
  return fromDecimal(toDecimal(a).plus(toDecimal(b)))
}

export function subQuantity(a: Quantity, b: Quantity): Quantity {
  return fromDecimal(toDecimal(a).minus(toDecimal(b)))
}

export function multiplyQuantity(a: Quantity, b: Quantity): Quantity {
  return fromDecimal(toDecimal(a).times(toDecimal(b)))
}

export function sumQuantity(values: Iterable<Quantity>): Quantity {
  let total = new D(0)
  for (const value of values) total = total.plus(toDecimal(value))
  return fromDecimal(total)
}

export function compareQuantity(a: Quantity, b: Quantity): -1 | 0 | 1 {
  return toDecimal(a).comparedTo(toDecimal(b)) as -1 | 0 | 1
}

export function isZeroQuantity(quantity: Quantity): boolean {
  return toDecimal(quantity).isZero()
}

export function isNegativeQuantity(quantity: Quantity): boolean {
  return toDecimal(quantity).isNegative() && !toDecimal(quantity).isZero()
}

export function invertRate(rate: Quantity): Quantity {
  const d = toDecimal(rate)
  if (d.isZero()) throw new MoneyError('cannot invert a zero rate')
  return fromDecimal(new D(1).dividedBy(d))
}

export function crossRate(base: Quantity, quote: Quantity): Quantity {
  const b = toDecimal(base)
  if (b.isZero()) throw new MoneyError('cannot cross through a zero rate')
  return fromDecimal(toDecimal(quote).dividedBy(b))
}

export function minorFromMajorString(major: Quantity, minorUnitsPerMajor = 100): Minor {
  return roundToMinor(toDecimal(major).times(minorUnitsPerMajor))
}

export function majorStringFromMinor(amountMinor: Minor, minorUnitsPerMajor = 100): Quantity {
  return fromDecimal(new D(assertMinor(amountMinor)).dividedBy(minorUnitsPerMajor))
}
